import { api } from '@shared/api';
import { reportAppError } from '@shared/reportAppError';
import {
  postGuestPricingImpressionsBatch,
  type GuestPricingImpressionEventPayload,
} from './guestPricingApiHelpers';
import { getOrCreateGuestPricingSessionId } from './guestPricingSessionId';

const DEBOUNCE_MS = 450;
const MAX_BATCH = 40;

const pendingByRestaurant = new Map<
  string,
  GuestPricingImpressionEventPayload[]
>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const clearTimer = (restaurantId: string) => {
  const t = timers.get(restaurantId);
  if (t) {
    clearTimeout(t);
    timers.delete(restaurantId);
  }
};

export type FlushGuestPricingQueueOptions = {
  /** Prefer `fetch(..., { keepalive: true })` so unload / SPA unmount is less likely to drop the POST. */
  unloadSafe?: boolean;
};

const flushRestaurant = async (
  restaurantId: string,
  options?: FlushGuestPricingQueueOptions,
): Promise<void> => {
  clearTimer(restaurantId);
  const pending = pendingByRestaurant.get(restaurantId);
  if (!pending?.length) {
    return;
  }

  pendingByRestaurant.set(restaurantId, []);

  const sessionId = getOrCreateGuestPricingSessionId(restaurantId);

  if (options?.unloadSafe) {
    api.postKeepalive(
      `/restaurants/${restaurantId}/guest-pricing-impressions`,
      {
        sessionId,
        events: pending,
      },
    );
    return;
  }

  try {
    await postGuestPricingImpressionsBatch({
      restaurantId,
      sessionId,
      events: pending,
    });
  } catch (error) {
    reportAppError(error);
  }
};

const scheduleFlush = (restaurantId: string): void => {
  clearTimer(restaurantId);
  timers.set(
    restaurantId,
    setTimeout(() => {
      void flushRestaurant(restaurantId);
    }, DEBOUNCE_MS),
  );
};

export const enqueueGuestPricingImpression = (
  restaurantId: string,
  event: GuestPricingImpressionEventPayload,
): void => {
  const list = pendingByRestaurant.get(restaurantId) ?? [];
  list.push(event);
  pendingByRestaurant.set(restaurantId, list);

  if (list.length >= MAX_BATCH) {
    void flushRestaurant(restaurantId);
    return;
  }

  scheduleFlush(restaurantId);
};

export const flushGuestPricingImpressionsForRestaurant = (
  restaurantId: string,
  options?: FlushGuestPricingQueueOptions,
): Promise<void> => flushRestaurant(restaurantId, options);

export const flushAllGuestPricingImpressionQueues = (
  options?: FlushGuestPricingQueueOptions,
): void => {
  for (const restaurantId of pendingByRestaurant.keys()) {
    void flushRestaurant(restaurantId, options);
  }
};
